import { useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { DataGrid, type ColumnDef, type SortingState } from '@/components/DataGrid';
import { ExportButton } from '@/components/ExportButton';
import { Icon } from '@/components/Icon';
import { Status } from '@/components/Status';
import { useEntrepreneursSearch } from '@/hooks/useEntrepreneurs';
import { formatDateTime, formatPhone } from '@/lib/format';
import type { Entrepreneur, ReconStatus, RecruitmentSource, SortKey } from '@/api/entrepreneurs';

/**
 * Dossiers — entrepreneur search page, built on the unified DataGrid wrapper.
 *
 * Every filter and the sort are **server-side**: the grid only ever holds one
 * page, so refining it locally would silently filter that page alone while the
 * row count kept describing the whole set. The toolbar above the grid is
 * therefore the single place where a filter is expressed, and the per-column
 * filter row / global search box of the grid stay off for the same reason.
 *
 * Two date windows are exposed, and they are not interchangeable:
 *   • « Survenu » filters on when the event happened (occurred_at) — the field
 *     the producer declares, which can be backdated;
 *   • « Enregistré » filters on when master data received it (recorded_at) —
 *     the ingestion timestamp, which is what an operator looks at to answer
 *     "what landed in the referential this week?".
 */
export function DossiersPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [commune, setCommune] = useState('');
  const [sector, setSector] = useState('');
  const [reconStatus, setReconStatus] = useState<ReconStatus>('');
  // Origine de recrutement : la question qui a motivé tout le chantier —
  // distinguer ce que nos équipes ont recruté de ce qui vient d'Orange et de
  // ses partenaires.
  const [origin, setOrigin] = useState<RecruitmentSource>('');
  // Date inputs hold a plain `YYYY-MM-DD`; they're widened to an ISO instant
  // at query time (see dayStart / dayAfter).
  const [occurredFrom, setOccurredFrom] = useState('');
  const [occurredTo, setOccurredTo] = useState('');
  const [recordedFrom, setRecordedFrom] = useState('');
  const [recordedTo, setRecordedTo] = useState('');
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 50 });
  // Les filtres de dates sont repliés par défaut : ils servent rarement, et
  // une barre de plus au-dessus de la grille repousse d'autant les lignes hors
  // de l'écran — ce qui donne une grille en apparence vide.
  const [showDates, setShowDates] = useState(false);
  const [sorting, setSorting] = useState<SortingState>([]);

  const query = useEntrepreneursSearch({
    phone: phone || undefined,
    name: name || undefined,
    commune: commune || undefined,
    sector: sector || undefined,
    reconStatus: reconStatus || undefined,
    recruitmentSource: origin || undefined,
    lastEventFrom: dayStart(occurredFrom),
    lastEventTo: dayAfter(occurredTo),
    recordedFrom: dayStart(recordedFrom),
    recordedTo: dayAfter(recordedTo),
    sort: SORT_KEYS[sorting[0]?.id ?? ''],
    order: sorting[0] ? (sorting[0].desc ? 'desc' : 'asc') : undefined,
    limit: pagination.pageSize,
    offset: pagination.pageIndex * pagination.pageSize,
  });

  const data = query.data?.items ?? [];
  const total = query.data?.total ?? 0;
  const hasDateFilter = !!(occurredFrom || occurredTo || recordedFrom || recordedTo);

  function onRowClick(e: Entrepreneur) {
    void navigate({ to: '/dossiers/$phone', params: { phone: e.phoneNumber } });
  }

  function resetPagination() {
    if (pagination.pageIndex !== 0) setPagination((p) => ({ ...p, pageIndex: 0 }));
  }

  function onSortingChange(next: SortingState) {
    setSorting(next);
    resetPagination();
  }

  function clearDates() {
    setOccurredFrom('');
    setOccurredTo('');
    setRecordedFrom('');
    setRecordedTo('');
    resetPagination();
  }

  const columns = useMemo<ColumnDef<Entrepreneur>[]>(
    () => [
      {
        accessorFn: (e) => displayName(e),
        id: 'name',
        header: 'Entreprenant',
        size: 280,
        Cell: ({ row }) => (
          <div className="cell-stack">
            <span className="pri">{displayName(row.original)}</span>
            <span className="ph">{formatPhone(row.original.phoneNumber)}</span>
          </div>
        ),
      },
      {
        accessorFn: (e) => e.commune ?? '',
        id: 'commune',
        header: 'Commune',
        size: 130,
        Cell: ({ row }) => row.original.commune ?? '—',
      },
      {
        accessorFn: (e) => e.primaryActivity ?? e.sector ?? '',
        id: 'activity',
        header: 'Activité',
        size: 180,
        Cell: ({ row }) => row.original.primaryActivity ?? row.original.sector ?? '—',
      },
      {
        accessorKey: 'externalRefs',
        header: 'Références',
        enableSorting: false,
        enableColumnFilter: false,
        size: 160,
        Cell: ({ row }) => (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {Object.keys(row.original.externalRefs ?? {})
              .slice(0, 3)
              .map((k) => (
                <span key={k} className="ref">{k.toUpperCase()}</span>
              ))}
          </div>
        ),
      },
      {
        accessorKey: 'eventCount',
        header: 'Événements',
        size: 110,
        mantineTableHeadCellProps: { align: 'right' },
        mantineTableBodyCellProps: { align: 'right' },
        Cell: ({ cell }) => (
          <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: 'var(--ink)' }}>
            {cell.getValue<number>()}
          </span>
        ),
      },
      {
        accessorKey: 'lastEventAt',
        header: 'Survenu le',
        size: 160,
        Cell: ({ cell }) => (
          <span style={{ font: '600 12px var(--mono)', color: 'var(--ink-3)' }}>
            {formatDateTime(cell.getValue<string>())}
          </span>
        ),
      },
      {
        // updated_at is bumped on every ingestion, so it *is* the recorded_at
        // of the most recent event — the column the « Enregistré » filter acts on.
        accessorKey: 'updatedAt',
        header: 'Enregistré le',
        size: 160,
        Cell: ({ cell }) => (
          <span style={{ font: '600 12px var(--mono)', color: 'var(--ink-3)' }}>
            {formatDateTime(cell.getValue<string>())}
          </span>
        ),
      },
      {
        accessorKey: 'recruitmentSource',
        header: 'Recruté par',
        size: 150,
        Cell: ({ row }) => {
          const src = row.original.recruitmentSource;
          const partner = row.original.recruitmentPartner;
          return (
            <div className="cell-stack">
              <span className="pri" style={{ fontSize: 12.5 }}>
                {ORIGIN_LABELS[src] ?? src}
              </span>
              {partner ? <span className="ph">{partner}</span> : null}
            </div>
          );
        },
      },
      {
        accessorKey: 'reconciliationStatus',
        header: 'Statut',
        size: 130,
        Cell: ({ cell }) => {
          const status = cell.getValue<string>();
          return (
            <Status kind={status === 'OK' ? 'ok' : 'warn'}>{RECON_LABELS[status] ?? status}</Status>
          );
        },
      },
    ],
    [],
  );

  return (
    <div className="fade-in">
      <div className="page-head">
        <div>
          <div className="pt">{t('pages.dossiers.title')}</div>
          <div className="ps">{t('pages.dossiers.subtitle')}</div>
        </div>
        <div className="ph-right">
          <ExportButton
            rows={data}
            filenamePrefix="dossiers"
            columns={[
              { header: 'Téléphone', value: (e) => e.phoneNumber },
              { header: 'Nom affiché', value: (e) => e.displayName ?? '' },
              { header: 'Nom de commerce', value: (e) => e.businessName ?? '' },
              { header: 'Activité', value: (e) => e.primaryActivity ?? '' },
              { header: 'Secteur', value: (e) => e.sector ?? '' },
              { header: 'Commune', value: (e) => e.commune ?? '' },
              { header: 'Événements', value: (e) => e.eventCount },
              { header: 'Survenu le', value: (e) => e.lastEventAt ?? '' },
              { header: 'Enregistré le', value: (e) => e.updatedAt },
              { header: 'Recruté par', value: (e) => ORIGIN_LABELS[e.recruitmentSource] ?? e.recruitmentSource },
              { header: 'Partenaire', value: (e) => e.recruitmentPartner ?? '' },
              { header: 'Statut réconciliation', value: (e) => e.reconciliationStatus },
              { header: 'Statut enrichissement', value: (e) => e.enrichmentStatus },
            ]}
          />
        </div>
      </div>

      <div className="toolbar">
        <label className="input grow">
          <Icon name="search" />
          <input
            placeholder="Nom, commerce, activité"
            value={name}
            onChange={(e) => { setName(e.target.value); resetPagination(); }}
          />
        </label>
        <label className="input">
          <Icon name="phone" />
          <input
            placeholder="+2250707000000"
            value={phone}
            onChange={(e) => { setPhone(e.target.value); resetPagination(); }}
            style={{ fontFamily: 'var(--mono)', width: 200 }}
          />
        </label>
        <label className="input">
          <Icon name="pin" />
          <input
            placeholder="Commune"
            value={commune}
            onChange={(e) => { setCommune(e.target.value); resetPagination(); }}
            style={{ width: 150 }}
          />
        </label>
        <label className="input">
          <Icon name="box" />
          <input
            placeholder="Secteur"
            value={sector}
            onChange={(e) => { setSector(e.target.value); resetPagination(); }}
            style={{ width: 150 }}
          />
        </label>
        <div className="seg">
          {ORIGIN_TABS.map(([value, label]) => (
            <button
              key={value || 'all-origins'}
              className={origin === value ? 'on' : ''}
              onClick={() => { setOrigin(value); resetPagination(); }}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="seg">
          {RECON_TABS.map(([value, label]) => (
            <button
              key={value || 'all'}
              className={reconStatus === value ? 'on' : ''}
              onClick={() => { setReconStatus(value); resetPagination(); }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="toolbar" style={{ marginBottom: showDates ? 10 : 16 }}>
        <button
          type="button"
          className={hasDateFilter || showDates ? 'btn' : 'btn ghost'}
          onClick={() => setShowDates((v) => !v)}
        >
          <Icon name="filter" />
          Filtres par date{hasDateFilter ? ' · actifs' : ''}
        </button>
        {hasDateFilter && (
          <button type="button" className="btn ghost" onClick={clearDates}>
            Effacer les dates
          </button>
        )}
      </div>

      <div className="toolbar" style={{ display: showDates ? 'flex' : 'none' }}>
        {/* A <label> may own a single control; these pills hold two dates each,
            so each input carries its own aria-label instead. */}
        <div className="input">
          <Icon name="activity" />
          <span className="filter-label">Survenu</span>
          <input
            type="date"
            aria-label="Survenu à partir du"
            value={occurredFrom}
            onChange={(e) => { setOccurredFrom(e.target.value); resetPagination(); }}
          />
          <span className="filter-sep">→</span>
          <input
            type="date"
            aria-label="Survenu jusqu'au"
            value={occurredTo}
            onChange={(e) => { setOccurredTo(e.target.value); resetPagination(); }}
          />
        </div>
        <div className="input">
          <Icon name="download" />
          <span className="filter-label">Enregistré</span>
          <input
            type="date"
            aria-label="Enregistré à partir du"
            value={recordedFrom}
            onChange={(e) => { setRecordedFrom(e.target.value); resetPagination(); }}
          />
          <span className="filter-sep">→</span>
          <input
            type="date"
            aria-label="Enregistré jusqu'au"
            value={recordedTo}
            onChange={(e) => { setRecordedTo(e.target.value); resetPagination(); }}
          />
        </div>
        {hasDateFilter && (
          <button type="button" className="btn ghost" onClick={clearDates}>
            Effacer les dates
          </button>
        )}
      </div>

      {query.isError && (
        <div className="err-msg" style={{ marginBottom: 14 }}>
          La liste n'a pas pu être chargée :{' '}
          {query.error instanceof Error ? query.error.message : 'erreur inconnue'}
          <button
            type="button"
            className="btn ghost"
            style={{ marginLeft: 12 }}
            onClick={() => void query.refetch()}
          >
            Réessayer
          </button>
        </div>
      )}

      <DataGrid
        columns={columns}
        data={data}
        isLoading={query.isLoading}
        pagination={pagination}
        onPaginationChange={setPagination}
        rowCount={total}
        sorting={sorting}
        onSortingChange={onSortingChange}
        onRowClick={onRowClick}
        // Filters live in the toolbar and run on the server. The grid's own
        // filter row and search box would only see the loaded page.
        disableColumnFilters
        disableGlobalFilter
        emptyMessage={
          query.isError
            ? "La liste n'a pas pu être chargée — voir le message ci-dessus."
            : 'Aucun dossier ne correspond à ces filtres.'
        }
      />
    </div>
  );
}

/** Grid column id → the `sort` enum the API accepts. */
const SORT_KEYS: Record<string, SortKey | undefined> = {
  name: 'name',
  commune: 'commune',
  activity: 'activity',
  eventCount: 'eventCount',
  lastEventAt: 'lastEvent',
  updatedAt: 'recorded',
  reconciliationStatus: 'status',
};

const ORIGIN_LABELS: Record<string, string> = {
  MAFA_TERRAIN: 'Nos équipes',
  ORANGE_PARTENAIRE: 'Orange / partenaires',
  ID30: 'PDE.CI',
  UNKNOWN: 'Inconnue',
};

const ORIGIN_TABS: [RecruitmentSource, string][] = [
  ['', 'Toutes origines'],
  ['MAFA_TERRAIN', 'Nos équipes'],
  ['ORANGE_PARTENAIRE', 'Orange / partenaires'],
];

const RECON_LABELS: Record<string, string> = {
  OK: 'Réconcilié',
  PENDING_REVIEW: 'À arbitrer',
  CONFLICT: 'En conflit',
};

const RECON_TABS: [ReconStatus, string][] = [
  ['', 'Tous'],
  ['OK', 'Réconciliés'],
  ['PENDING_REVIEW', 'À arbitrer'],
  ['CONFLICT', 'Conflits'],
];

/**
 * `YYYY-MM-DD` → ISO instant at the start of that day, in the operator's own
 * timezone: they filter on calendar days as they read them on screen, not on
 * UTC days. Returns undefined for an empty input so the filter is dropped.
 */
function dayStart(day: string): string | undefined {
  if (!day) return undefined;
  const d = new Date(`${day}T00:00:00`);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

/**
 * Same, shifted to the next midnight. The API's upper bounds are exclusive, so
 * this is what makes « jusqu'au 15 » include everything that happened on the 15th.
 */
function dayAfter(day: string): string | undefined {
  if (!day) return undefined;
  const d = new Date(`${day}T00:00:00`);
  if (Number.isNaN(d.getTime())) return undefined;
  d.setDate(d.getDate() + 1);
  return d.toISOString();
}

function displayName(e: Entrepreneur): string {
  return (
    e.displayName ||
    e.businessName ||
    [e.firstname, e.lastname].filter(Boolean).join(' ') ||
    e.phoneNumber
  );
}
